import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { SegTabs } from "@/components/SegTabs";
import { AppHeader } from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { TABS } from "@/components/drawing-settings/shared";
import { GeneralTab } from "@/components/drawing-settings/GeneralTab";
import { CatalogueTab } from "@/components/drawing-settings/CatalogueTab";
import { EdgesTab } from "@/components/drawing-settings/EdgesTab";
import { CornersTab } from "@/components/drawing-settings/CornersTab";
import { BumpsTab } from "@/components/drawing-settings/BumpsTab";
import { CutoutsTab } from "@/components/drawing-settings/CutoutsTab";
import { KitsTab } from "@/components/drawing-settings/KitsTab";
import { LinesTab } from "@/components/drawing-settings/LinesTab";

export default function DrawingSettings() {
  const nav = useNavigate();
  const [tab, setTab] = useState("Edges");
  const [d, setD] = useState(null);
  const [opts, setOpts] = useState({ shapes: [], colors: [], services: [], products: [] });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/settings/drawing");
    setD(data.drawing);
    setOpts({
      shapes: data.shapes,
      colors: data.colors,
      services: data.services,
      products: data.products || [],
    });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (!d)
    return (
      <div className="grain min-h-screen">
        <AppHeader title="Drawing" />
      </div>
    );

  const set = (patch) => setD((cur) => ({ ...cur, ...patch }));
  const setPart = (key, patch) =>
    setD((cur) => ({ ...cur, [key]: { ...cur[key], ...patch } }));
  const setEdge = (i, patch) =>
    setD((cur) => ({
      ...cur,
      edges: cur.edges.map((e, idx) => (idx === i ? { ...e, ...patch } : e)),
    }));
  const setCutout = (i, patch) =>
    setD((cur) => ({
      ...cur,
      cutouts: cur.cutouts.map((c, idx) => (idx === i ? { ...c, ...patch } : c)),
    }));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings/drawing", d);
      setD(data.drawing);
      toast.success("Drawing settings saved");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not save",
      );
    }
    setBusy(false);
  };

  const serviceOptions = opts.services.map((s) => ({
    value: s.id,
    label: `${s.name} · $${Number(s.cost_estimate || 0).toFixed(2)}/${s.unit}`,
  }));

  const productOptions = opts.products.map((p) => ({
    value: p.id,
    label: `${p.name} · $${Number(p.price || 0).toFixed(2)}`,
  }));

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Drawing" />
      <main className="relative z-10 pb-36 pt-6">
        <div
          className="rounded-lg border border-zinc-800 bg-zinc-900 p-5"
          data-testid="drawing-settings-header"
        >
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
            Drawing
          </h1>
          <p className="mt-1 text-sm text-zinc-500">
            Edge styles, corner and cut-out defaults, and what the shop drawing
            prints
          </p>
          <div className="mt-5 border-t border-zinc-800 pt-4">
            <SegTabs
              testId="drawing-tabs"
              tabs={TABS.map((t) => ({ key: t, label: t, testId: `drawing-tab-${t.toLowerCase()}` }))}
              value={tab}
              onChange={(t) => setTab(t.key)}
            />
          </div>
        </div>

        {tab === "Edges" && (
          <EdgesTab d={d} set={set} setEdge={setEdge} opts={opts} serviceOptions={serviceOptions} />
        )}
        {tab === "General" && <GeneralTab d={d} setPart={setPart} />}
        {tab === "Catalogue" && <CatalogueTab d={d} setPart={setPart} />}
        {tab === "Corners" && <CornersTab d={d} setPart={setPart} />}
        {tab === "Bumps" && <BumpsTab d={d} setPart={setPart} />}
        {tab === "Cutouts" && (
          <CutoutsTab
            d={d}
            set={set}
            setCutout={setCutout}
            opts={opts}
            serviceOptions={serviceOptions}
            productOptions={productOptions}
          />
        )}
        {tab === "Kits" && <KitsTab d={d} set={set} />}
        {tab === "Lines" && <LinesTab d={d} setPart={setPart} opts={opts} />}
      </main>

      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-zinc-800 bg-zinc-950/95 px-4 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-7xl justify-end gap-2">
          <Button
            data-testid="drawing-cancel"
            variant="outline"
            onClick={() => nav("/settings")}
            className="h-12 border-zinc-700 bg-transparent text-xs uppercase tracking-widest hover:bg-zinc-800"
          >
            Cancel
          </Button>
          <Button
            data-testid="drawing-save"
            disabled={busy}
            onClick={save}
            className="h-12 bg-indigo-500 px-8 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
          >
            {busy ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
    </div>
  );
}
